import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer } from 'antd';
import ViewerPropertiesPanel from 'Components/UI/ViewerPropertiesPanel';
import { BimPanelProperties } from '../../ifcProperties';
import emptyProperties from '../../../../Static/images/empty-properties.png';
import styles from '../PropertiesDrawer/PropertiesDrawer.module.scss';
import '../FederatedDrawer/FederatedDrawer.css';

interface IIfcPropertiesDrawer {
  properties: BimPanelProperties | null;
  visible: boolean;
  onClose: () => void;
}

/**
 * Painel de propriedades do federado quando o motor é o IFC.
 *
 * Separado do `PropertiesDrawer` (que é da Autodesk, e o `InternalViewer` também
 * usa) porque os dois motores diferem no que têm a mostrar:
 *
 * - a Autodesk manda unidade por propriedade e um elemento por vez;
 * - o IFC não manda unidade nenhuma (ela não viaja no artefato do data model) e
 *   agrega a seleção inteira, o que traz "*Varia*" com os valores individuais e
 *   somas que precisam se identificar como soma.
 *
 * Por isso o miolo aqui é o `ViewerPropertiesPanel`, o mesmo dos viewers 2D e 3D
 * — a casca (posição, largura, header fixo) segue a do painel da Autodesk para
 * os dois motores ficarem no mesmo lugar da tela.
 */
const IfcPropertiesDrawer: FC<IIfcPropertiesDrawer> = ({
  properties,
  visible,
  onClose,
}) => {
  const { t } = useTranslation('federatedViewer');

  // Categorias internas (`_Nome`, `Nome_`) nunca foram para a tela — mesmo
  // critério do painel da Autodesk.
  const categories = useMemo(() => (
    properties?.categories.filter((category) => (
      !category.categoryName?.startsWith('_') && !category.categoryName?.endsWith('_')
    )) ?? []
  ), [properties]);

  return (
    <Drawer
      placement="right"
      closable={false}
      // Esc é do viewer (sai da medição, volta a cena ao padrão); sem isto o
      // Ant fecha o painel junto, e a seleção some com o painel.
      keyboard={false}
      onClose={() => onClose()}
      visible={visible}
      destroyOnClose={true}
      width={300}
      height={541}
      style={{
        position: 'absolute',
        height: 541,
        marginRight: 12,
        marginTop: 126,
        border: visible ? '1px solid #EAEDF0' : 'none',
        borderTop: visible ? 'none' : undefined,
        borderRight: visible ? '1px solid #EAEDF0' : undefined,
      }}
      className={styles['drawer']}
      zIndex={2}
      mask={false}
      title={
        properties && properties.count > 1
          ? t('properties.titleWithCount', { count: properties.count })
          : t('properties.title')
      }
      headerStyle={{
        fontSize: '14px',
        fontWeight: 600,
        lineHeight: '19px',
        color: '#44566C',
        position: 'fixed',
        height: '43px',
        width: '300px',
        display: 'flex',
        alignItems: 'center',
        paddingLeft: '-4px',
        background: '#FFFFFF',
        zIndex: 2,
      }}
    >
      {/* Compensa o header, que é fixo e sai do fluxo. */}
      <div style={{ height: '43px' }} />
      <ViewerPropertiesPanel
        name={properties?.name}
        categories={categories}
        emptyMessage={t('properties.noObjectSelected')}
        emptyImage={emptyProperties}
      />
    </Drawer>
  );
};

export default IfcPropertiesDrawer;
